/**
 * Édition d'une leçon : contenu riche, ressources, mise en ligne.
 *
 * Le contenu part au serveur sous la forme d'un document ProseMirror, validé
 * là-bas contre une allowlist de nœuds (docs/11 §3). L'éditeur — TipTap, dont
 * c'est le format natif — n'expose que ce que cette allowlist accepte : il ne
 * peut donc pas fabriquer un document que l'enregistrement refuserait après
 * coup, sur un texte déjà écrit.
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { FileUploader } from "@/features/storage/components/FileUploader";
import type { StoredFile } from "@/features/storage/api/useStorage";
import {
  useAttachResource,
  useCreateExercise,
  useDetachResource,
  useLesson,
  useUpdateLesson,
} from "@/features/catalog/api/useCatalog";
import { ExerciseEditor } from "@/features/catalog/components/ExerciseEditor";
import { ResourceList } from "@/features/catalog/components/ResourceList";
import {
  asDocument,
  EMPTY_DOCUMENT,
  toStored,
  type ContentDocument,
} from "@/shared/richtext/document";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

const FIELD_CLASSES =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm " +
  "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

export function LessonEditorPage() {
  const { t } = useTranslation("catalog");
  const navigate = useNavigate();
  const { courseId, lessonId } = useParams({
    from: "/teach/teach/courses/$courseId/lessons/$lessonId",
  });

  const lesson = useLesson(courseId, lessonId);
  const updateLesson = useUpdateLesson(courseId, lessonId);
  const attachResource = useAttachResource(courseId);
  const detachResource = useDetachResource(courseId);
  const createExercise = useCreateExercise(courseId);
  const errorMessage = useApiErrorMessage();

  const [title, setTitle] = useState("");
  // `undefined` — et non `null` — tant que la leçon n'est pas arrivée.
  // `RichTextEditor` lit son contenu **à la construction** ; le monter avant
  // que le document ne soit chargé donnerait un éditeur vide sur une leçon qui
  // n'est pas vide, et l'enseignant écraserait son propre texte au premier
  // enregistrement. Les deux absences ne veulent pas dire la même chose, et
  // c'est au type de l'empêcher.
  const [content, setContent] = useState<ContentDocument | null | undefined>(undefined);
  const [resource, setResource] = useState({ type: "video", title: "", url: "" });
  // Un fichier téléversé n'est **pas** encore une ressource : il est déposé,
  // vérifié, et attend qu'on l'attache. Le garder ici entre les deux gestes
  // laisse l'enseignant nommer sa ressource avant de valider — et un dépôt
  // qu'il abandonne reste un fichier à lui, que le balayage horaire ne touche
  // pas puisqu'il a bien été confirmé.
  const [uploaded, setUploaded] = useState<StoredFile | null>(null);
  const [exerciseTitle, setExerciseTitle] = useState("");

  // Le type décide de tout le reste du formulaire : une URL pour un lien ou une
  // vidéo, un fichier déposé pour un PDF ou une image. La distinction est celle
  // de `ResourceType.is_hosted` côté serveur ; la recopier ici est une
  // duplication assumée — le formulaire doit savoir quel champ afficher avant
  // d'appeler quoi que ce soit — mais elle reste **la seule**, et c'est le
  // serveur qui refuse un couple incohérent.
  const isHosted = resource.type === "pdf" || resource.type === "image";

  // Initialisé une seule fois, à l'arrivée de la leçon : réinitialiser à chaque
  // `lesson.data` écraserait ce que l'enseignant est en train d'écrire dès
  // qu'une invalidation de cache passe.
  useEffect(() => {
    if (lesson.data === undefined) {
      return;
    }
    setTitle(lesson.data.lesson.title);
    setContent(asDocument(lesson.data.content));
  }, [lesson.data?.lesson.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (lesson.isPending) {
    return <Skeleton className="mx-auto my-8 h-96 w-full max-w-4xl" label={t("lesson.content")} />;
  }
  if (lesson.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={lesson.error} />
      </div>
    );
  }

  const detail = lesson.data;
  const isPublished = detail.lesson.status === "published";

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{detail.lesson.title}</h1>
        <Button
          variant="ghost"
          onClick={() => {
            void navigate({ to: "/teach/courses/$courseId", params: { courseId } });
          }}
        >
          {t("lesson.backToCourse")}
        </Button>
      </header>

      <Card title={t("lesson.content")}>
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium" htmlFor="lesson-title">
            {t("lesson.title")}
          </label>
          <input
            id="lesson-title"
            className={FIELD_CLASSES}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        {content !== undefined && (
          <RichTextEditor
            label={t("lesson.content")}
            documentKey={detail.lesson.id}
            content={content}
            onChange={setContent}
            minHeight="min-h-64"
          />
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            disabled={updateLesson.isPending}
            onClick={() => {
              // Un contenu effacé part en document **vide** et non en `null` :
              // `content: null` vaut « ne touche pas au contenu » pour une mise
              // à jour partielle, et l'enseignant qui vide sa leçon la
              // retrouverait intacte au rechargement.
              updateLesson.mutate({ title, content: toStored(content) ?? EMPTY_DOCUMENT });
            }}
          >
            {t("editor.save")}
          </Button>
          <Button
            variant="secondary"
            disabled={updateLesson.isPending}
            onClick={() => {
              updateLesson.mutate({ status: isPublished ? "draft" : "published" });
            }}
          >
            {isPublished ? t("lesson.unpublish") : t("lesson.publish")}
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={detail.lesson.is_preview}
              onChange={(event) => {
                updateLesson.mutate({ is_preview: event.target.checked });
              }}
            />
            {t("lesson.isPreview")}
          </label>
          {updateLesson.isSuccess && (
            <span className="text-sm text-green-700 dark:text-green-400">{t("editor.saved")}</span>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("lesson.previewHint")}</p>
        {updateLesson.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(updateLesson.error)}
          </Alert>
        )}
      </Card>

      <Card title={t("resources.title")}>
        <ResourceList
          resources={detail.resources}
          onRemove={(resourceId) => {
            detachResource.mutate({ lessonId, resourceId });
          }}
        />

        <div className="grid gap-3 sm:grid-cols-[auto_1fr_2fr_auto] sm:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="resource-type">
              {t("resources.type")}
            </label>
            <select
              id="resource-type"
              className={FIELD_CLASSES}
              value={resource.type}
              onChange={(event) => {
                setResource({ ...resource, type: event.target.value });
              }}
            >
              <option value="video">{t("resources.video")}</option>
              <option value="link">{t("resources.link")}</option>
              <option value="pdf">{t("resources.pdf")}</option>
              <option value="image">{t("resources.image")}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="resource-title">
              {t("resources.label")}
            </label>
            <input
              id="resource-title"
              className={FIELD_CLASSES}
              value={resource.title}
              onChange={(event) => {
                setResource({ ...resource, title: event.target.value });
              }}
            />
          </div>
          <div>
            {isHosted ? (
              <>
                <span className="mb-1 block text-sm font-medium">{t("resources.file")}</span>
                <FileUploader
                  purpose="lesson_resource"
                  onUploaded={(file) => {
                    setUploaded(file);
                    // Le nom d'origine devient l'intitulé par défaut : c'est
                    // celui que l'enseignant reconnaît, et le laisser vide
                    // l'obligerait à retaper ce qu'il vient de choisir.
                    setResource((current) =>
                      current.title === "" ? { ...current, title: file.original_name } : current,
                    );
                  }}
                />
                {uploaded !== null && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {uploaded.original_name}
                  </p>
                )}
              </>
            ) : (
              <>
                <label className="mb-1 block text-sm font-medium" htmlFor="resource-url">
                  {t("resources.url")}
                </label>
                <input
                  id="resource-url"
                  className={FIELD_CLASSES}
                  value={resource.url}
                  placeholder="https://…"
                  onChange={(event) => {
                    setResource({ ...resource, url: event.target.value });
                  }}
                />
              </>
            )}
          </div>
          <Button
            disabled={
              (isHosted ? uploaded === null : resource.url.trim() === "") ||
              attachResource.isPending
            }
            onClick={() => {
              attachResource.mutate(
                {
                  lessonId,
                  type: resource.type as "link" | "video" | "pdf" | "image",
                  title: resource.title,
                  // L'un **ou** l'autre, jamais les deux : c'est le type qui
                  // décide, et le serveur tient l'invariant.
                  ...(isHosted ? { fileId: uploaded?.id } : { url: resource.url }),
                },
                {
                  onSuccess: () => {
                    setResource({ type: resource.type, title: "", url: "" });
                    setUploaded(null);
                  },
                },
              );
            }}
          >
            {t("resources.attach")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {isHosted ? t("resources.fileHint") : t("resources.providersHint")}
        </p>
        {attachResource.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(attachResource.error)}
          </Alert>
        )}
      </Card>

      {detail.exercises.map((exercise) => (
        <ExerciseEditor
          key={exercise.id}
          courseId={courseId}
          lessonId={lessonId}
          exercise={exercise}
        />
      ))}

      <Card title={t("exercises.add")}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grow">
            <label className="mb-1 block text-sm font-medium" htmlFor="exercise-title">
              {t("exercises.titleLabel")}
            </label>
            <input
              id="exercise-title"
              className={FIELD_CLASSES}
              value={exerciseTitle}
              onChange={(event) => {
                setExerciseTitle(event.target.value);
              }}
            />
          </div>
          <Button
            disabled={exerciseTitle.trim() === "" || createExercise.isPending}
            onClick={() => {
              createExercise.mutate(
                { lessonId, title: exerciseTitle },
                {
                  onSuccess: () => {
                    setExerciseTitle("");
                  },
                },
              );
            }}
          >
            {t("exercises.add")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("exercises.addHint")}</p>
        {createExercise.isError && (
          <Alert tone="error" className="mt-3">
            {errorMessage(createExercise.error)}
          </Alert>
        )}
      </Card>
    </div>
  );
}
